import { parseDbTimestamp } from "@repo/shared";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { get } from "@/lib/api/_client";
import { useApiKeys } from "@/lib/hooks/use-api-keys";
import { useT } from "@/lib/i18n/use-t";
import { log } from "@/lib/logger/browser-logger";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { ApiKeyDialog } from "./api-key-dialog";
import { T } from "./api-key-wrapper.i18n";
import { ApiKeysLoader } from "./api-keys-loader";
import { ApiKeysTable } from "./api-keys-table";

type JwtTokenResponse = {
  success: boolean;
  token: string;
  expiresIn: string;
  expiresAt: string | null;
  payload: {
    userId: string;
    tenantId: string;
    tenantSlug: string;
    email: string;
  };
};

export function ApiKeyWrapper() {
  const { formatDateTime } = useRegionalFormat();
  const t = useT(T);
  const { apiKeys, isLoading } = useApiKeys();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [jwtToken, setJwtToken] = useState<JwtTokenResponse | null>(null);
  const [jwtLoading, setJwtLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchJwtToken();
  }, []);

  const fetchJwtToken = async () => {
    setJwtLoading(true);
    try {
      const data = await get<JwtTokenResponse>("/internal/security/jwt-token");
      setJwtToken(data);
    } catch (err) {
      log.error({ err }, "failed to fetch jwt token");
    } finally {
      setJwtLoading(false);
    }
  };

  const handleCopyToken = async () => {
    if (jwtToken?.token) {
      await navigator.clipboard.writeText(jwtToken.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (isLoading) {
    return <ApiKeysLoader />;
  }

  return (
    <>
      {/* JWT Token Section */}
      <div className="space-y-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{t.devJwtToken}</h2>
            <p className="text-sm text-muted-foreground">{t.devJwtTokenDescription}</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchJwtToken} loading={jwtLoading}>
            {t.refresh}
          </Button>
        </div>

        {jwtToken && (
          <>
            <div className="flex items-center gap-4">
              <Button variant="default" onClick={handleCopyToken}>
                {copied ? t.copied : t.copyJwtToken}
              </Button>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>
                  {t.expires} {jwtToken.expiresIn}
                </span>
                {jwtToken.expiresAt && (
                  <span className="text-xs">
                    ({formatDateTime(parseDbTimestamp(jwtToken.expiresAt))})
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <Label className="text-xs text-muted-foreground">{t.userId}</Label>
                <p className="text-sm font-mono mt-1">{jwtToken.payload.userId}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t.email}</Label>
                <p className="text-sm mt-1">{jwtToken.payload.email}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t.tenantId}</Label>
                <p className="text-sm font-mono mt-1">{jwtToken.payload.tenantId}</p>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">{t.tenantSlug}</Label>
                <p className="text-sm font-mono mt-1">{jwtToken.payload.tenantSlug}</p>
              </div>
            </div>
          </>
        )}

        {!jwtToken && !jwtLoading && (
          <div className="text-sm text-muted-foreground">{t.failedToGenerateToken}</div>
        )}
      </div>

      {/* API Keys Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{t.apiKeysTitle}</h2>
            <p className="text-sm text-muted-foreground">{t.apiKeysDescription}</p>
          </div>
          <Button onClick={() => setDialogOpen(true)}>{t.createApiKey}</Button>
        </div>

        <ApiKeysTable apiKeys={apiKeys} />
      </div>

      <ApiKeyDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
}
